import { ReactNode } from 'react';
import slugify from 'slugify';
import { useContextSelector } from 'use-context-selector';
import _ from 'lodash';
import ReactHtmlParser from 'react-html-parser';

import { LabelContext } from './LabelContext';

function useLabel() {
  const { config, labels, wildcards } = useContextSelector(
    LabelContext,
    (c) => c
  );

  type IOptions = {
    additionalWildcards?: object;
  };

  function label(text: string | ReactNode, options?: IOptions) {
    return getLabel(text, options) || text;
  }

  function plainLabel(text: string | ReactNode, options?: IOptions): string {
    const _label = label(text, options);
    return Array.isArray(_label) ? _label.join('') : _label;
  }

  function getLabel(text: string | ReactNode, options?: IOptions) {
    const defaultLanguage = config.language || config.defaultLanguage;
    const key = getLabelKey(text);

    let label;
    try {
      // get label from database (labels.json)
      // for some reasons, must use || because sometimes comes undefined with _.get
      label =
        _.get(labels, `${key}.${defaultLanguage}`) ||
        // @ts-ignore
        labels[key][defaultLanguage];

      // replace wildcards (if exists)
      label = replaceWildcards(label, {
        additionalWildcards: options?.additionalWildcards,
      });
    } catch (err) {}
    return !!label ? ReactHtmlParser(label) : label;
  }

  function getLabelKey(text: string | ReactNode): string {
    function replaceAll(text: string, search: string, replace: string): string {
      return text.split(search).join(replace);
    }

    function sanitizeKey(text: string): string {
      //const options = { remove: /[*+~.()'"!:@<>]/g, strict: true };
      const options = {};

      text = text.replace(/<\/?[^>]+(>|$)/g, ''); // remove html tags
      text = slugify(text, options);
      text = replaceAll(text, '"', ''); // remove "
      text = replaceAll(text, '.', '_'); // replace . for _
      text = replaceAll(text, ':', '_'); // replace : for _
      text = replaceAll(text, '!', '_'); // replace ! for _
      text = replaceAll(text, '?', '_'); // replace ? for _
      return text;
    }

    try {
      // @ts-ignore
      return sanitizeKey(text);
    } catch (err) {
      // goes here if probably has JSX code
      try {
        let convertedText = jsxToString(text);
        return sanitizeKey(convertedText);
      } catch (err) {
        // @ts-ignore
        return text;
      }
    }
  }

  function replaceWildcards(text: string, options: IOptions) {
    const { additionalWildcards = {} } = options;
    if (!hasWildcards(text)) return text;
    const allWildcards: object = { ...wildcards, ...additionalWildcards };
    const wildcardsFound = getWildcards(text, { additionalWildcards });
    _.forEach(wildcardsFound, (wildcard) => {
      text = _.replace(text, wildcard, _.get(allWildcards, wildcard));
    });
    return text;
  }

  function hasWildcards(text: string): boolean {
    return _.includes(text, '[[');
  }

  function getWildcards(text: string, options: any = {}) {
    const { additionalWildcards = {} } = options;
    const allWildcards = { ...wildcards, ...additionalWildcards };
    const wildcardsFound: string[] = [];
    _.forEach(_.keys(allWildcards), (wildcard) => {
      while (_.includes(text, wildcard)) {
        wildcardsFound.push(wildcard);
        text = _.replace(text, wildcard, '');
      }
    });
    return wildcardsFound;
  }

  function jsxToString(jsx: any): string {
    try {
      const string = _.map(Array.isArray(jsx) ? jsx : [jsx], (el) => {
        return el?.type && el?.props?.children
          ? `<${typeToTag(el.type, el?.props)}>${jsxToString(
              el.props.children
            )}</${typeToTag(el.type)}>`
          : el;
      });
      return string.join('');
    } catch (err) {
      throw err;
    }

    function typeToTag(type: any, props: object = {}) {
      function propsToAttr(props: any) {
        const keys = _.filter(_.keys(props), (key) => key !== 'children');
        return _.map(keys, (key) => `${key}="${props[key]}"`).join(' ');
      }
      const tags = ['span', 'strong', 'div', 'a'];
      return tags.includes(type) ? `${type} ${propsToAttr(props)}` : '';
    }
  }

  return { label, plainLabel, getLabelKey, jsxToString };
}

export default useLabel;
